<template>
	<el-row>
		<el-col :span="18" style="text-align: left;">
			<div class="base-form">			
				<h2>
					{{ isPickerModeEnabled ? 'Выбор сотрудника' : 'Сотрудники' }}
				</h2>				
				<el-button type="primary" v-if="!isPickerModeEnabled" :icon="circleIcon" size="large" @click="onAddUserClick">
					Новый сотрудник
				</el-button>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="4" style="text-align: left;">
			<div class="base-form">
				<el-select v-model="tablePositionsFilter" multiple placeholder="Фильтр по должностям">
					<el-option v-for="p in employeePositions"
						:key="p.id"
						:value="p.id"
						:label="p.name">
					</el-option>
				</el-select>
			</div>
		</el-col>
		<el-col :span="12">
			&nbsp;
		</el-col>
		<el-col :span="8" style="text-align: right;">
			<div class="base-form">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" placeholder="Поиск по имени, фамилии, отчеству, телефону или e-mail"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="24">
			<el-table ref="tableRef"
				row-key="id"
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@row-click="onTableRowClick"
				@sort-change="onTableSortingChange">
				<el-table-column v-if="!isPickerModeEnabled" type="selection" />
				<el-table-column width="100" align="center" v-if="isPickerModeEnabled && pickerModeOnItemPickedHandler">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => pickerModeOnItemPickedHandler(scope.row)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column width="80" label="№" align="center" sortable prop="id" column-key="id" />
				<el-table-column label="Дата создания" :formatter="(r) => dateFormatter(r.date)" sortable prop="id" column-key="id"/>
				<el-table-column label="Фамилия" sortable prop="lastname" column-key="lastname" />
				<el-table-column label="Имя" prop="firstname" sortable column-key="firstname" />
				<el-table-column label="Отчество" prop="middlename" sortable column-key="middlename" />
				<el-table-column label="Дата приема" :formatter="(r) => dateFormatter(r.hireDate)" sortable prop="hireDate" column-key="hireDate" />
				<el-table-column width="200" label="Должность, грейд" sortable prop="position" column-key="position" >
					<template #default="scope">
						{{ scope.row.position.name }}
						<div>
							<el-text type="info" size="small">{{ scope.row.positionGrade?.name }}</el-text>
						</div>
					</template>
				</el-table-column>
				<el-table-column width="200" label="Дата рождения" :formatter="(r) => dateFormatter(r.birthdate)" sortable prop="birthdate" column-key="birthdate" />
				<el-table-column width="200" label="Телефон" prop="phone" />				
				<el-table-column width="200" label="Пользователь" column-key="user" v-if="!isPickerModeEnabled">
					<template #default="scope">
						{{ scope.row.user?.login }}
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import { date as dateFormatter } from '@/helpers/formatters';
import UserModel from '@/models/UserModel';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import eventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import EmployeeModel from '@/models/EmployeeModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class EmployeesView extends Vue {
	/** constants */	
	circleIcon = CirclePlus;
	searchIcon = Search;
	employeePositions = new Array<any>();
	df = dateFormatter;
	isPickerModeEnabled = false;
	pickerModeOnItemPickedHandler: Function | null = null;

	/** props */
	params!: Array<any>;

	/** view-models */	
	tableLoading = false;
	tableData: Array<UserModel> = [];
	tableEmployeesWithoutUser = false;
	tablePositionsFilter: Array<number> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'lastname';
	tableSortOrder = 'ascending';
	
	watchTableSearchQuery: WatchStopHandle | null = null;
	watchTablePositionsFilter: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.fetchEmployeesAsync();
		this.fetchEmployeePositionsAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchEmployeesAsync()
		);

		this.watchTablePositionsFilter = watch(
			() => this.tablePositionsFilter, 
			() => this.fetchEmployeesAsync()
		);

		eventHelper.setEventListener('employeesUpdate', () => this.fetchEmployeesAsync());
	}

	unmounted() {

		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		if (this.watchTablePositionsFilter) {
			this.watchTablePositionsFilter();
		}

		eventHelper.removeEventListener('employeesUpdate', () => this.fetchEmployeesAsync());
	}

	initView() {
		if (!this.params?.length) {
			return;
		}

		const pickerConfiguration = this.params[0] as PickerConfigurationModel;

		this.isPickerModeEnabled = pickerConfiguration.isPickerModeEnabled;
		this.pickerModeOnItemPickedHandler = pickerConfiguration.onItemPickedCallback;
	}

	onAddUserClick() {
		const addUserTab = TabsDictionary.employeeAdd;
		const isAddingEmployeeView = true;
		addUserTab.params = [ null, isAddingEmployeeView ];
		store.commit('addTab', addUserTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder ==  a.order;
		this.fetchEmployeesAsync();
	}

	onTableRowClick(employee: EmployeeModel) {
		const addUserTab = TabsDictionary.employeeView;
		const isAddingEmployeeView = false;
		addUserTab.params = [ employee, isAddingEmployeeView ];
		store.commit('addTab', addUserTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchEmployeesAsync() {
		try {
			this.tableLoading = true;
			const filterObject = {
				orderBy: this.tableSortColumn,
				orderAsc: this.tableSortOrder == 'ascending',
				query: this.tableSearchQuery,
				positions: this.tablePositionsFilter?.join(',') ?? null,
				filterEmployeesWithUser: this.isPickerModeEnabled ? false : null	
			};
			const jwt = store.getters.jwt
			const employeesHttpClient = axiosHelper.createInstance(jwt);
			const response = await employeesHttpClient.get('/api/employees', { params: filterObject });
			this.tableData = response.data;
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка соединения, не удалось загрузить список сотрудников`,
					type: 'error',
				});
			} else {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка загрузка списка сотрудников`,
					type: 'error',
				});
			}
			console.error('Error loading employees list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async fetchEmployeePositionsAsync() {
		try {
			const jwt = store.getters.jwt
			const employeePositionsHttpClient = axiosHelper.createInstance(jwt);
			const response = await employeePositionsHttpClient.get('/api/employees/positions');
			this.employeePositions = response.data;			
		} catch (e: any) {
			console.error('Error loading employee positions list', e);
		}
	}

	dateFormatter(date: Date) {		
		return dateFormatter.dateTime(date, true, true);
	}
}
</script>